import { useLocation } from "react-router-dom";
import { X } from "lucide-react";
import { useExtracted } from "next-intl";
import { cn } from "@/lib/utils";
import { usePWAInstall } from "@/hooks/usePWAInstall";

export function InstallBanner() {
  const te = useExtracted();
  const { pathname } = useLocation();
  const { showInstallBanner, isIOS, canPrompt, promptInstall, dismissBanner } =
    usePWAInstall();
  const isWorkspace = pathname.startsWith("/workspace");

  if (!showInstallBanner || pathname === "/" || pathname === "/purchase")
    return null;

  return (
    <div
      className={cn(
        "pointer-events-none fixed inset-x-0 z-50 px-3 sm:px-4",
        isWorkspace
          ? "bottom-[calc(6.25rem+env(safe-area-inset-bottom))]"
          : "bottom-[calc(0.75rem+env(safe-area-inset-bottom))] sm:bottom-5",
      )}
    >
      <div className="pointer-events-auto relative mx-auto max-w-[25rem] overflow-hidden rounded-[1.375rem] border border-white/[0.09] bg-[rgba(7,9,12,0.9)] p-3 shadow-[0_18px_56px_rgba(0,0,0,0.52)] backdrop-blur-[24px]">
        <div className="pointer-events-none absolute inset-x-10 top-0 h-px bg-[linear-gradient(90deg,transparent,rgba(166,231,246,0.24),rgba(171,154,255,0.2),transparent)]" />

        <div className="flex items-center gap-3">
          <div className="flex size-12 shrink-0 items-center justify-center rounded-[1rem] border border-white/[0.08] bg-white/[0.035]">
            <img
              alt="αβΓ✻1"
              className="size-8 object-contain"
              height={32}
              src="/icon-192.png"
              width={32}
            />
          </div>

          <div className="min-w-0 flex-1">
            <p className="text-[13px] font-semibold tracking-[0.01em] text-white/90">
              {te("Install αβΓ✻1")}
            </p>
            {isIOS ? (
              <p className="mt-0.5 text-[12px] leading-5 text-white/48">
                {te("Tap")}{" "}
                <ShareIcon className="-mt-0.5 inline size-3.5 text-cyan-300/90" />{" "}
                {te('then "Add to Home Screen"')}
              </p>
            ) : (
              <p className="mt-0.5 text-[12px] leading-5 text-white/48">
                {te("Add to your home screen for the full app experience")}
              </p>
            )}
          </div>

          {canPrompt ? (
            <button
              className="shrink-0 cursor-pointer rounded-full border border-cyan-100/14 bg-cyan-100/[0.055] px-3 py-2 text-xs font-medium text-cyan-50/86 transition-[border-color,background-color,color,transform] hover:-translate-y-px hover:border-cyan-100/24 hover:bg-cyan-100/[0.09] hover:text-white"
              type="button"
              onClick={promptInstall}
            >
              {te("Install")}
            </button>
          ) : null}

          <button
            aria-label={te("Close")}
            className="flex size-9 shrink-0 cursor-pointer items-center justify-center rounded-full text-white/36 transition-[background-color,color,transform] hover:rotate-3 hover:bg-white/[0.055] hover:text-white/78"
            type="button"
            onClick={dismissBanner}
          >
            <X size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}

/** Safari share icon (square with up arrow) */
function ShareIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      viewBox="0 0 24 24"
    >
      <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />
      <polyline points="16 6 12 2 8 6" />
      <line x1="12" x2="12" y1="2" y2="15" />
    </svg>
  );
}
